// « Click a chip → POURQUOI » (owner, 2026-10-10): the last 5 things the agent
// did for this client, each with its reason in one plain line — logged facts
// only (worker/src/lib/agentWhy.ts). Desktop: a side panel. Phone: full screen.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { pilotageApi } from '@/lib/api';
import { adminOrderHref } from '@/lib/orderHref';
import { waitShort, type Chip } from './journey';

const STATE: Record<string, string> = {
  red: 'border-status-stuck-border bg-status-stuck-bg text-status-stuck',
  amber: 'border-status-waiting-border bg-status-waiting-bg text-status-waiting',
  blue: 'border-info/45 bg-info/10 text-info',
};

export function WhyPanel({ chip, now, full, onClose, onOpenChat }: {
  chip: Chip; now: number; full: boolean; onClose: () => void; onOpenChat: (chatId: string) => void;
}) {
  const { t, lang } = useT();
  const qc = useQueryClient();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pilotage', 'why', chip.key],
    queryFn: () => pilotageApi.why({ chat: chip.chatId, order: chip.orderId }),
    refetchInterval: 30_000,
  });
  const handled = useMutation({
    mutationFn: () => pilotageApi.inboxAct(chip.items.map((i) => ({ key: i.key, reason: i.reason, chatId: i.chatId, orderId: i.orderId })), 'handled'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pilotage'] }); onClose(); },
  });
  const ignored = useMutation({
    mutationFn: () => pilotageApi.ignore([chip.orderId ? { kind: 'order', ref: chip.orderId } : { kind: 'chat', ref: String(chip.chatId) }]),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pilotage'] }); onClose(); },
  });

  const time = (iso: string) => {
    const d = new Date(iso);
    const day = d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca', day: '2-digit', month: '2-digit' });
    const hm = d.toLocaleTimeString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit' });
    return { day, hm };
  };
  const state = chip.tone === 'red' ? t('Bloquée chez nous depuis {w} — {s}', { w: waitShort(chip.sinceIso, now), s: t(chip.client.stageLabel) })
    : chip.tone === 'amber' ? t('Attend ta décision depuis {w} — {s}', { w: waitShort(chip.sinceIso, now), s: t(chip.items[0]?.title ?? chip.client.stageLabel) })
      : chip.tone === 'blue' ? t('L’agent agit pour elle en ce moment')
        : null;
  const orderHref = chip.shopifyOrderId ? adminOrderHref({ shopifyOrderId: chip.shopifyOrderId }) : null;
  const btn = 'inline-flex items-center justify-center rounded-lg border px-3 min-h-[40px] text-[13px] font-medium cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

  return (
    <div className={full ? 'fixed inset-0 z-50 bg-bg-card flex flex-col' : 'fixed inset-y-0 right-0 z-40 w-[380px] max-w-full bg-bg-card border-l border-border-subtle shadow-xl flex flex-col'}
      role="dialog" aria-modal={full} aria-label={t('Pourquoi')}>
      <div className="flex items-start gap-3 px-4 py-3.5 border-b border-border-subtle">
        <div className="min-w-0 flex-1">
          <p className="font-heading text-[18px] leading-tight text-text-primary truncate">{chip.name}</p>
          <p className="text-[12px] text-text-tertiary truncate">
            {[chip.orderName, chip.client.phone, t(chip.client.stageLabel)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label={full ? t('Retour') : t('Fermer')}
          className="h-10 w-10 shrink-0 rounded-full border border-border-subtle text-text-secondary grid place-items-center cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <span aria-hidden className="text-[16px] leading-none">{full ? '←' : '×'}</span>
        </button>
      </div>
      {state && <p className={`mx-4 mt-3 rounded-lg border px-3 py-2 text-[12.5px] font-medium ${STATE[chip.tone]}`}>{state}</p>}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-2">
        <p className="text-[11.5px] text-text-tertiary py-1.5">{t('Ce que l’agent a fait, et pourquoi')}</p>
        {isLoading && <p className="text-[12.5px] text-text-tertiary py-3">{t('Chargement…')}</p>}
        {isError && <p className="text-[12.5px] text-status-waiting py-3">{t('Impossible de lire l’historique. Réessaie dans un instant.')}</p>}
        {data && data.lines.length === 0 && <p className="text-[12.5px] text-text-tertiary py-3">{t('Rien d’enregistré pour elle ces 30 derniers jours.')}</p>}
        <ol>
          {data?.lines.map((l, i) => {
            const tm = time(l.at);
            return (
              <li key={`${l.at}-${i}`} className="grid grid-cols-[52px_minmax(0,1fr)] gap-2 py-2.5 border-b border-dashed border-border-subtle last:border-b-0">
                <span className="text-[11.5px] text-text-tertiary tabular-nums leading-tight">{tm.day}<br />{tm.hm}</span>
                <span className="min-w-0">
                  <span className={`block text-[13px] ${l.kind === 'handoff' || l.kind === 'blocked' || l.kind === 'failed' ? 'text-status-stuck font-medium' : 'text-text-primary'}`}>{t(l.title)}</span>
                  {l.why && <span className="block text-[12px] text-text-secondary">{t(l.why)}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="grid grid-cols-2 gap-2 px-4 py-3 border-t border-border-subtle">
        {chip.chatId
          ? <button type="button" onClick={() => onOpenChat(chip.chatId!)} className={`${btn} border-brand-500 bg-brand-500 text-[#1C150E] dark:text-[#1C150E] font-semibold hover:bg-brand-600`}>{t('Ouvrir la conversation')}</button>
          : <span />}
        {orderHref
          ? <Link to={orderHref} className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>{t('Ouvrir la commande')}</Link>
          : <span />}
        {chip.items.length > 0 && (
          <button type="button" onClick={() => handled.mutate()} disabled={handled.isPending}
            className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>✓ {t('Traité')}</button>
        )}
        <button type="button" onClick={() => ignored.mutate()} disabled={ignored.isPending}
          className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated ${chip.items.length ? '' : 'col-span-2'}`}>✗ {t('Ignorer')}</button>
      </div>
      {(handled.isError || ignored.isError) && (
        <p className="px-4 pb-3 text-[12px] text-status-stuck">{t('Ça n’a pas marché. Réessaie.')}</p>
      )}
    </div>
  );
}
